/**
 * Writer subscription tiers: limits and feature flags (mirrored in functions/writerPlanConfig.js for enforcement).
 */

export const WRITER_PLAN = Object.freeze({
  FREE: "free",
  BASIC: "basic",
  PRO: "pro",
});

const LEGACY_TO_CANONICAL = Object.freeze({
  reader_to_writer: WRITER_PLAN.BASIC,
  starter: WRITER_PLAN.BASIC,
  premium: WRITER_PLAN.BASIC,
  elite: WRITER_PLAN.PRO,
});

/**
 * @param {string|undefined|null} raw
 * @returns {"free"|"basic"|"pro"}
 */
export function normalizeWriterPlan(raw) {
  const s = String(raw || WRITER_PLAN.FREE).trim().toLowerCase();
  if (LEGACY_TO_CANONICAL[s]) return LEGACY_TO_CANONICAL[s];
  if (s === WRITER_PLAN.BASIC || s === WRITER_PLAN.PRO || s === WRITER_PLAN.FREE) return s;
  return WRITER_PLAN.FREE;
}

const PLAN_CONFIG = Object.freeze({
  [WRITER_PLAN.FREE]: Object.freeze({
    maxPublishedPerMonth: 5,
    editorTier: "minimal",
    allowPrivateVisibility: false,
    allowPremiumVisibility: false,
    analyticsLevel: "none",
    allowScheduledPublish: false,
    allowBranding: false,
    allowWriterTheme: false,
  }),
  [WRITER_PLAN.BASIC]: Object.freeze({
    maxPublishedPerMonth: 50,
    editorTier: "standard",
    allowPrivateVisibility: true,
    allowPremiumVisibility: true,
    analyticsLevel: "basic",
    allowScheduledPublish: false,
    allowBranding: false,
    allowWriterTheme: false,
  }),
  [WRITER_PLAN.PRO]: Object.freeze({
    maxPublishedPerMonth: Number.POSITIVE_INFINITY,
    editorTier: "full",
    allowPrivateVisibility: true,
    allowPremiumVisibility: true,
    analyticsLevel: "full",
    allowScheduledPublish: true,
    allowBranding: true,
    allowWriterTheme: true,
  }),
});

/**
 * @param {object|null|undefined} user — app user or raw RTDB user record
 */
/**
 * These exist in plan config but are not a finished product yet.
 * Keep the server fields; do not offer them in the UI.
 */
const HIDDEN_UNTIL_READY = Object.freeze({
  allowPremiumVisibility: false,
  allowScheduledPublish: false,
  allowBranding: false,
  allowWriterTheme: false,
});

export function getWriterPlanFeatures(user) {
  const planId = normalizeWriterPlan(user?.subscriptions?.writerPlan ?? user?.writerPlan);
  const base = PLAN_CONFIG[planId] || PLAN_CONFIG[WRITER_PLAN.FREE];
  return { planId, ...base, ...HIDDEN_UNTIL_READY };
}

/** Jodit toolbar button groups per tier */
export const JODIT_BUTTONS_BY_TIER = Object.freeze({
  minimal: [
    "bold",
    "italic",
    "underline",
    "|",
    "ul",
    "ol",
    "|",
    "link",
    "paragraph",
  ],
  standard: [
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "|",
    "ul",
    "ol",
    "|",
    "fontsize",
    "brush",
    "|",
    "align",
    "|",
    "link",
    "image",
    "table",
    "|",
    "hr",
    "eraser",
    "copyformat",
    "|",
    "paragraph",
    "lineHeight",
  ],
  full: [
    "bold",
    "italic",
    "underline",
    "strikethrough",
    "|",
    "ul",
    "ol",
    "|",
    "fontsize",
    "font",
    "brush",
    "|",
    "align",
    "|",
    "link",
    "image",
    "video",
    "table",
    "|",
    "hr",
    "fullsize",
    "source",
    "|",
    "eraser",
    "copyformat",
    "|",
    "paragraph",
    "lineHeight",
  ],
});

/**
 * Billed amounts in INR (keep in sync with `functions/razorpayWriterPlans.js` PLAN_PRICE_PAISE ÷ 100).
 */
export const WRITER_PLAN_BILLED_PRICE_INR = Object.freeze({
  [WRITER_PLAN.FREE]: 0,
  [WRITER_PLAN.BASIC]: 199,
  [WRITER_PLAN.PRO]: 999,
});

export const WRITER_PLAN_PRICING_COPY = Object.freeze({
  [WRITER_PLAN.FREE]: { label: "Free", priceInr: 0, blurb: "Try the platform" },
  [WRITER_PLAN.BASIC]: { label: "Basic", blurb: "Grow your catalog" },
  [WRITER_PLAN.PRO]: { label: "Pro", blurb: "Unlimited publishing" },
});

/** Short bullet lists for marketing / public pricing page (aligned with PLAN_CONFIG). */
export const WRITER_PLAN_MARKETING_BULLETS = Object.freeze({
  [WRITER_PLAN.FREE]: [
    "Up to 5 published pieces per month (UTC)",
    "Essential editor (formatting and links)",
    "Public visibility",
    "Likes, comments, and a writing dashboard",
  ],
  [WRITER_PLAN.BASIC]: [
    "Up to 50 published pieces per month",
    "Standard editor (images, tables, alignment)",
    "Public visibility",
    "Basic analytics on your dashboard",
  ],
  [WRITER_PLAN.PRO]: [
    "Unlimited publishing",
    "Full editor (video, source, full screen)",
    "Public visibility",
    "Full analytics on your dashboard",
  ],
});
